import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, MessageSquare, Loader2, ThumbsUp, ThumbsDown, Minus } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { value: "content_difficulty", label: "Content Difficulty" },
  { value: "ui_ux", label: "UI/UX" },
  { value: "concept_clarity", label: "Concept Clarity" },
  { value: "general", label: "General" },
];

export default function Feedback() {
  const [text, setText] = useState("");
  const [category, setCategory] = useState("general");
  const [submitting, setSubmitting] = useState(false);
  const [feedbackList, setFeedbackList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/feedback`).then(r => { setFeedbackList(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleSubmit = async () => {
    if (!text.trim()) { toast.error("Please write your feedback"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/feedback`, { text: text.trim(), category });
      toast.success("Feedback submitted! Thank you.");
      setFeedbackList(prev => [{ id: res.data.id, text: text.trim(), user_category: category, ai_categorization: res.data.categorization, created_at: new Date().toISOString() }, ...prev]);
      setText("");
    } catch (err) {
      toast.error("Failed to submit feedback");
    }
    setSubmitting(false);
  };

  const sentimentIcon = (sentiment) => {
    if (sentiment === "positive") return <ThumbsUp size={14} className="text-emerald-400" />;
    if (sentiment === "negative") return <ThumbsDown size={14} className="text-red-400" />;
    return <Minus size={14} className="text-muted-foreground" />;
  };

  return (
    <div className="p-8 lg:p-12 space-y-8 animate-fade-in" data-testid="feedback-page">
      <div>
        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">Feedback</h1>
        <p className="text-muted-foreground mt-2 text-base">
          Help us improve your preparation experience
        </p>
      </div>

      {/* Submit Feedback */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Share Your Thoughts</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="feedback-category-select" className="w-[240px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map(c => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Textarea
            data-testid="feedback-text-input"
            placeholder="What's on your mind? Share feedback about questions, difficulty, UI, or anything else..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="min-h-[120px] bg-secondary/30 border-border focus:border-primary"
          />

          <Button
            data-testid="submit-feedback-btn"
            onClick={handleSubmit}
            disabled={submitting || !text.trim()}
            className="bg-primary hover:bg-primary/90 gap-2"
          >
            {submitting ? <><Loader2 size={16} className="animate-spin" /> Analyzing...</> : <><Send size={16} /> Submit Feedback</>}
          </Button>
        </CardContent>
      </Card>

      {/* Feedback History */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="font-heading text-lg">
            <MessageSquare size={18} className="inline mr-2" />
            Previous Feedback ({feedbackList.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-secondary rounded animate-pulse" />)}</div>
          ) : feedbackList.length === 0 ? (
            <p className="text-muted-foreground text-sm py-8 text-center">No feedback submitted yet</p>
          ) : (
            <div className="space-y-3">
              {feedbackList.map((f, i) => (
                <div key={f.id || i} data-testid={`feedback-item-${i}`} className="p-4 rounded-sm border border-border bg-secondary/20">
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-sm leading-relaxed flex-1">{f.text}</p>
                    <div className="flex items-center gap-2 ml-3 shrink-0">
                      {sentimentIcon(f.ai_categorization?.sentiment)}
                      <Badge variant="outline" className="text-xs capitalize">
                        {f.ai_categorization?.primary_category?.replace(/_/g, " ") || f.user_category}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{f.created_at ? new Date(f.created_at).toLocaleDateString() : ""}</span>
                    {f.ai_categorization?.priority && (
                      <Badge variant="outline" className={`text-xs ${
                        f.ai_categorization.priority === "high" ? "border-red-500/50 text-red-400" :
                        f.ai_categorization.priority === "medium" ? "border-amber-500/50 text-amber-400" :
                        "border-border text-muted-foreground"
                      }`}>
                        {f.ai_categorization.priority} priority
                      </Badge>
                    )}
                    {f.ai_categorization?.summary && (
                      <span className="italic">"{f.ai_categorization.summary}"</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
